import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import CustomButton from "../../../components/customButton/customButton";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import Loader from "../../../components/loader";
import GridStyles from "../../../assets/css/grid.styles";
import CustomDialog from "../../../components/customDialog/customDialog";
import {
  deleteLookupValue,
  getLookupsList,
} from "../../../reducers/lookupReducer";
import { CustomTableColumnsProps } from "../../../components/customDataTable/customTableColumnsProps";
import {
  DELETE_ITEM_ERROR_MESSAGE,
  DELETE_LOOKUP,
} from "../../../constants/actions";
import CustomIcon from "../../../components/customIcon/customIcon";
import { PermissionsActions } from "../../../constants/permissionsActions";
import { PermissionsActionsModel } from "../../../models/models/permissionsActionsModel";
import CustomCollapseDataTable from "../../../components/customCollapseDataTable/customCollapseDataTable";
import { UserPermissionsModel } from "../../../models/models/userPermissionsModel";

const LookupsListForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authToken = useSelector((state: RootState) => state.auth.token);
  const lookupListState = useSelector(
    (state: RootState) => state.lookup.lookupsList
  );
  const [take, setTake] = useState(5);
  const [skip, setSkip] = useState(1);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] = useState<any | null>(
    null
  );
  const [dataReloadFlag, setDataReloadFlag] = useState(false);
  const [key, setKey] = useState<number>(0);
  const [sortType, setSortType] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState<string | null>(null);

  const userPermissions: UserPermissionsModel[] = JSON.parse(
    localStorage.getItem("userPermissions") ?? "{}"
  );

  for (let i = 0; i < userPermissions.length; i++) {
    const { permission, pageUrl, actions } = userPermissions[i];
    if (permission === "All" || pageUrl === "/Lookups") {
      actions.forEach((action) => {
        PermissionsActions[
          action.toLowerCase() as keyof PermissionsActionsModel
        ] = true;
      });
      break;
    }
  }

  const columns: CustomTableColumnsProps[] = [
    { id: "name", label: "Name", width: "150", align: "left", visible: true },
    {
      id: "ordering",
      label: "Order",
      width: "150",
      align: "left",
      visible: true,
    },
    {
      id: "actions",
      visible: true,
      label: "Actions",
      width: "200",
      align: "center",
      renderCell: (params) => (
        <div>
          <>
            {PermissionsActions.add && (
              <CustomIcon
                tooltipText="Add"
                style={GridStyles.editIcon}
                onClick={() => handleAddClick(params.row)}
              >
                <AddIcon />
              </CustomIcon>
            )}
            {PermissionsActions.edit && (
              <CustomIcon
                tooltipText="Edit"
                onClick={() => handleEditParentClick(params.row)}
                style={GridStyles.editIcon}
              >
                <DriveFileRenameOutlineIcon />
              </CustomIcon>
            )}
          </>
        </div>
      ),
    },
  ];

  const childrenColumns: CustomTableColumnsProps[] = [
    {
      id: "valueEn",
      label: "Name",
      width: "150",
      align: "left",
      visible: true,
    },
    {
      id: "ordering",
      label: "Order",
      width: "150",
      align: "left",
      visible: true,
    },
    {
      id: "actions",
      visible: true,
      label: "Actions",
      width: "200",
      align: "center",
      renderCell: (params) => (
        <div>
          <>
            {PermissionsActions.edit && (
              <CustomIcon
                tooltipText="Edit"
                onClick={() => handleEditChildClick(params.row)}
                style={GridStyles.editIcon}
              >
                <DriveFileRenameOutlineIcon />
              </CustomIcon>
            )}
            {PermissionsActions.delete && (
              <CustomIcon
                tooltipText="Delete"
                onClick={() => handleDeleteClick(params.row)}
                style={GridStyles.deleteIcon}
              >
                <DeleteIcon />
              </CustomIcon>
            )}
          </>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!authToken) {
      navigate("/Signin");
    }
  }, [authToken]);

  useEffect(() => {
    dispatch(getLookupsList());
  }, [dispatch, dataReloadFlag]);

  const handleEditParentClick = (row: any) => {
    const editId = row ? row.id : 0;
    navigate(`/Lookup/ParentLookup/${editId}`);
  };

  const handleEditChildClick = (row: any) => {
    const editId = row ? row.id : 0;
    navigate(`/Lookup/ChildrenLookup/${editId}`);
  };

  const handleAddClick = (row?: any) => {
    const parentId = row ? row.id : 0;
    if (parentId === 0) {
      navigate(`/Lookup`);
    } else {
      navigate(`/Lookup/${parentId}`);
    }
  };

  const handleDeleteClick = (row: any) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = () => {
    const { id } = selectedRowToDelete;
    dispatch(deleteLookupValue({ data: id }))
      .unwrap()
      .then(() => {
        toast.success(`${DELETE_LOOKUP}`);
        setDataReloadFlag((prev) => !prev);
        setDeleteConfirmationOpen(false);
        setKey((prevKey) => prevKey + 1);
        setSkip(1);
      })
      .catch((error) => {
        toast.error(`${DELETE_ITEM_ERROR_MESSAGE} ${error}`);
        setDeleteConfirmationOpen(false);
      });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationOpen(false);
  };

  const handlePageChange = (newPage: number) => {
    setSkip(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTake(parseInt(event.target.value, 10));
    setSkip(1);
  };

  const handleSortChange = (columnId: string) => {
    setSortType(sortType === "asc" ? "desc" : "asc");
    setSortBy(columnId);
  };

  return (
    <div style={GridStyles.container}>
      <div style={GridStyles.header}>
        <div style={GridStyles.titleContainer}>
          <h1 style={GridStyles.title}>Lookups List</h1>
        </div>
        <div style={GridStyles.addButtonContainer}>
          {PermissionsActions.add && (
            <CustomButton
              type="button"
              style={GridStyles.addButton}
              onClick={() => handleAddClick()}
            >
              <AddIcon style={GridStyles.iconStyle} /> New Lookup
            </CustomButton>
          )}
        </div>
      </div>

      {lookupListState.isLoading && <Loader />}
      <CustomCollapseDataTable
        key={key}
        columns={columns}
        childrenColumns={childrenColumns}
        count={
          Array.isArray(lookupListState.data) ? lookupListState.data.length : 0
        }
        data={Array.isArray(lookupListState.data) ? lookupListState.data : []}
        isColored={true}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
      <CustomDialog
        text="Are you sure you want to delete this lookup?"
        open={deleteConfirmationOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Deletion"
        buttonTitle="Delete"
        icon={<DeleteIcon />}
      />
    </div>
  );
};

export default LookupsListForm;
